const COLORS = ['#2F5BEA', '#12B76A', '#F79009', '#E5484D', '#7A5AF8', '#06AED4', '#FDB022'];
const DURATION = 2600;
const GRAVITY = 0.32;
const DRAG = 0.985;

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  spin: number;
  /** ribbons flutter by squashing one axis */
  wobble: number;
  wobbleSpeed: number;
  round: boolean;
}

/**
 * A burst of confetti over the whole page, from (x, y) (default: the middle, a little above centre).
 * Draws on a temporary full-screen canvas that ignores the mouse and removes itself; skipped for people who ask
 * their system for less motion.
 */
export function fireConfetti(origin?: { x: number; y: number }) {
  if (typeof window === 'undefined' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const canvas = document.createElement('canvas');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  Object.assign(canvas.style, { position: 'fixed', inset: '0', width: `${width}px`, height: `${height}px`, pointerEvents: 'none', zIndex: '2000' });
  canvas.setAttribute('aria-hidden', 'true');
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.remove();
  ctx.scale(dpr, dpr);

  const x0 = origin?.x ?? width / 2;
  const y0 = origin?.y ?? height * 0.4;
  const pieces: Piece[] = [];
  const burst = (count: number, x: number, y: number, angle: number, spread: number, speed: number) => {
    for (let i = 0; i < count; i++) {
      const a = angle + (Math.random() - 0.5) * spread;
      const v = speed * (0.55 + Math.random() * 0.6);
      pieces.push({
        x,
        y,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        size: 6 + Math.random() * 6,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotation: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.3,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: 0.08 + Math.random() * 0.1,
        round: Math.random() < 0.25,
      });
    }
  };
  // one burst upwards from the click, plus two from the bottom corners for a page-wide celebration
  burst(90, x0, y0, -Math.PI / 2, Math.PI * 1.1, 15);
  burst(55, 0, height, -Math.PI / 3, Math.PI / 5, 24);
  burst(55, width, height, (-2 * Math.PI) / 3, Math.PI / 5, 24);

  const start = performance.now();
  const frame = (now: number) => {
    const elapsed = now - start;
    ctx.clearRect(0, 0, width, height);
    const fade = elapsed > DURATION - 600 ? Math.max(0, (DURATION - elapsed) / 600) : 1;
    for (const p of pieces) {
      p.vx *= DRAG;
      p.vy = p.vy * DRAG + GRAVITY;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.spin;
      p.wobble += p.wobbleSpeed;
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      if (p.round) {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2.4, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, (-p.size / 4) * Math.abs(Math.cos(p.wobble)), p.size, (p.size / 2) * Math.abs(Math.cos(p.wobble)) + 1);
      }
      ctx.restore();
    }
    if (elapsed < DURATION) requestAnimationFrame(frame);
    else canvas.remove();
  };
  requestAnimationFrame(frame);
}
